import React, { useMemo } from "react";
import Highcharts from "../../lib/highchartsExtended";
import type { Options as HCOptions } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../providers/ThemeProvider";
import type { LineSummary } from "../../services/ShiftAnalysisService";
import { computeShiftScore } from "./utils";
import { CountUp } from "./anim";
import Panel from "./Panel";

interface Props {
  line: LineSummary | null;
}

const Breakdown: React.FC<{
  label: string;
  pct: number;
  color: string;
}> = ({ label, pct, color }) => (
  <div className="flex items-center gap-2">
    <span
      className="text-[11px] w-20 shrink-0"
      style={{ color: "var(--text-secondary)" }}
    >
      {label}
    </span>
    <div
      className="flex-1 h-2 rounded-full overflow-hidden"
      style={{ backgroundColor: "var(--border-primary)" }}
    >
      <div
        className="h-full rounded-full transition-[width] duration-700 ease-out"
        style={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          backgroundColor: color,
        }}
      />
    </div>
    <span
      className="text-[11px] font-semibold tabular-nums w-9 text-right"
      style={{ color: "var(--text-primary)" }}
    >
      {Math.round(pct)}%
    </span>
  </div>
);

const ShiftScoreGauge: React.FC<Props> = ({ line }) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;
  const s = computeShiftScore(line);

  const options = useMemo<HCOptions>(() => {
    const track = isDark ? "#334155" : "#e5e7eb";
    return {
      chart: {
        type: "solidgauge",
        backgroundColor: "transparent",
        height: 210,
        spacing: [6, 0, 6, 0],
        animation: { duration: 900 },
      },
      title: undefined,
      credits: { enabled: false },
      tooltip: { enabled: false },
      pane: {
        center: ["50%", "50%"],
        size: "96%",
        startAngle: -135,
        endAngle: 135,
        background: [
          {
            outerRadius: "100%",
            innerRadius: "74%",
            backgroundColor: track,
            borderWidth: 0,
            shape: "arc",
          },
        ],
      },
      yAxis: {
        min: 0,
        max: 100,
        lineWidth: 0,
        tickWidth: 0,
        minorTickWidth: 0,
        tickAmount: 0,
        labels: { enabled: false },
        stops: [
          [0.25, "#ef4444"],
          [0.5, "#f59e0b"],
          [0.75, "#3b82f6"],
          [1, "#10b981"],
        ],
      },
      plotOptions: {
        solidgauge: {
          rounded: true,
          innerRadius: "74%",
          radius: "100%",
          dataLabels: { enabled: false },
        },
      },
      series: [
        {
          type: "solidgauge",
          name: "Shift Score",
          data: [s.score],
        },
      ],
    };
  }, [s.score, isDark]);

  return (
    <Panel label={t("shiftScore", "Shift Score")} accent={s.band.color}>
      <div className="relative">
        <HighchartsReact highcharts={Highcharts} options={options} />
        {/* Center overlay — matches pane center (50%/50%) */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <CountUp
            value={s.score}
            format={(n) => String(Math.round(n))}
            className="text-4xl font-extrabold tabular-nums leading-none"
            style={{ color: s.band.color }}
          />
          <span
            className="mt-1 text-xs font-semibold flex items-center gap-1"
            style={{ color: s.band.color }}
          >
            {s.band.emoji} {t(`scoreBand_${s.band.key}`, s.band.label)}
          </span>
          <span
            className="text-[10px]"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("outOf100", "out of 100")}
          </span>
        </div>
      </div>

      {/* Breakdown */}
      <div className="flex flex-col gap-2 mt-1">
        <Breakdown
          label={t("efficiency", "Efficiency")}
          pct={s.efficiencyPct}
          color="#10b981"
        />
        <Breakdown
          label={t("utilization", "Utilization")}
          pct={s.utilizationPct}
          color="#3b82f6"
        />
        <Breakdown
          label={t("reliability", "Reliability")}
          pct={s.reliabilityPct}
          color="#a855f7"
        />
      </div>

      <p
        className="text-[10px] mt-2 italic"
        style={{ color: "var(--text-secondary)" }}
      >
        {t(
          "shiftScoreFormula",
          "Efficiency 45% · Utilization 30% · Reliability 25%",
        )}
      </p>
    </Panel>
  );
};

export default ShiftScoreGauge;
